import { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { Button, Chip, IconButton, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  TableScroller,
  useAsync,
  useInterval,
} from '../../../components/common';
import { formatBytes } from '../../../lib/format';
import { saveBlob, useBuddyWrite } from '../../api/client';
import {
  CUSTOMER_DOC_LIMITS,
  knowledgeFor,
  uploadProblems,
  type CustomerDocument,
  type DocOwner,
} from '../../api/knowledge';
import { toneOf, when } from './shared';
import './knowledge.css';

/** Indexing runs in the background: the list is re-read this often while a document is pending or indexing. */
export const POLL_MS = 3000;
const indexing = (d: CustomerDocument) => d.status === 'pending' || d.status === 'indexing';

/**
 * A customer's own documents (ADR-0017) for one organization or one system: upload (several files, checked against
 * buddy3's limits before anything is sent), download, re-index, delete. Acts at once, independent of a surrounding
 * editor. `enabled` = the record's effective `customerDocs` flag: documents can be managed either way, but nothing
 * searches them while it is off. Write controls only with `ophub:buddy:admin`.
 */
export function CustomerDocuments({
  target,
  organizationId,
  systemId,
  enabled = true,
}: {
  target: string;
  organizationId?: string;
  systemId?: string;
  enabled?: boolean;
}) {
  const canWrite = useBuddyWrite();
  const owner: DocOwner | null = organizationId ? { organizationId } : systemId ? { systemId } : null;
  const ownerWord = organizationId ? 'organization' : 'system';
  const api = knowledgeFor(target).customerDocs;
  const docs = useAsync(
    () => (owner ? api.list(owner) : Promise.resolve([])),
    [target, organizationId, systemId]
  );
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  /** Share of the upload sent, 0..1 (null before the first progress event). */
  const [sent, setSent] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement | null>(null);

  const list = docs.data ?? [];
  useInterval(
    () => {
      if (owner)
        api
          .list(owner)
          .then(docs.setData)
          .catch(() => {
            /* the next tick tries again */
          });
    },
    POLL_MS,
    !!owner && list.some(indexing)
  );

  if (!owner)
    return <EmptyState title="Save this first" hint="Documents are uploaded once the record exists." />;

  const problems = uploadProblems(files);
  const total = files.reduce((s, f) => s + f.size, 0);
  const pick = (picked: File[]) => {
    // Picking again adds to the selection; the same name and size counts once.
    setFiles((prev) => [
      ...prev,
      ...picked.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size)),
    ]);
    if (input.current) input.current.value = '';
  };
  const upload = async () => {
    if (!files.length || problems.length) return;
    setUploading(true);
    setSent(null);
    setError(null);
    try {
      const added = await api.upload(owner, files, title, (done, all) => setSent(all ? done / all : null));
      docs.setData((prev) => [...added, ...(prev ?? []).filter((d) => !added.some((a) => a.id === d.id))]);
      setFiles([]);
      setTitle('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };
  const act = async (id: string, fn: () => Promise<void>) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bdk-cdocs" data-testid="customer-docs">
      <p className="nyk-card-intro">
        The customer&apos;s own documents (ways of working, customised processes). search_docs finds them next
        to the product documentation, for the users of this {ownerWord} only. PDF, Word, Markdown, text or
        HTML; up to {CUSTOMER_DOC_LIMITS.files} files of {formatBytes(CUSTOMER_DOC_LIMITS.fileBytes)} each,{' '}
        {formatBytes(CUSTOMER_DOC_LIMITS.totalBytes)} per upload.
      </p>
      {!enabled && (
        <p className="bdk-note nyk-tone-amber" data-testid="customer-docs-off">
          The Customer documents feature is off here: the documents are kept but not searched.
        </p>
      )}
      {canWrite && (
        <div className="bdk-upload" data-testid="customer-docs-upload">
          <div className="bdk-upload-row">
            <Button variant="secondary" icon={<Upload size={16} />} onClick={() => input.current?.click()}>
              Choose files
            </Button>
            <input
              ref={input}
              type="file"
              multiple
              hidden
              accept={CUSTOMER_DOC_LIMITS.extensions.join(',')}
              aria-label="Files to upload"
              onChange={(e) => pick(Array.from(e.target.files ?? []))}
            />
            {files.length > 0 && (
              <span className="nyk-ui-hint" data-testid="upload-summary">
                {files.length} file{files.length === 1 ? '' : 's'} · {formatBytes(total)}
              </span>
            )}
          </div>
          {files.length > 0 && (
            <ul className="bdk-files">
              {files.map((f, i) => (
                <li key={`${f.name}-${f.size}`}>
                  <span className="bdk-file-name">{f.name}</span>
                  <span className="nyk-ui-hint">{formatBytes(f.size)}</span>
                  <IconButton
                    label={`Remove ${f.name}`}
                    disabled={uploading}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  >
                    <X size={14} />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
          {problems.length > 0 && (
            <ul className="bdk-problems" role="alert" data-testid="upload-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {files.length === 1 && (
            <TextField
              label="Title (optional)"
              placeholder={files[0].name.replace(/\.[^.]+$/, '')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
          {files.length > 0 && (
            <div className="bdk-upload-row">
              <Button onClick={() => void upload()} loading={uploading} disabled={problems.length > 0}>
                {files.length > 1 ? `Upload ${files.length} files` : 'Upload'}
              </Button>
              {!uploading && (
                <Button variant="ghost" onClick={() => setFiles([])}>
                  Clear
                </Button>
              )}
              {uploading && (
                <span className="nyk-ui-hint" role="status">
                  {sent == null || sent < 1
                    ? `Sending ${formatBytes(total)}${sent != null ? ` · ${Math.round(sent * 100)} %` : ''}…`
                    : 'Sent; buddy3 checks every file before it stores any…'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      <ErrorBox error={docs.error ?? error} />
      <Loading loading={docs.loading && !docs.data} />
      {docs.data && list.length === 0 && (
        <EmptyState title="No documents yet" hint="Uploaded documents are indexed in the background." />
      )}
      {list.length > 0 && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="customer-docs-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th className="nyk-ui-num">Size</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.id} data-customer-doc={d.id}>
                  <td className="nyk-card-title">
                    <b>{d.title || d.fileName}</b>
                    <div className="nyk-ui-hint">{d.fileName}</div>
                  </td>
                  <td data-label="Status">
                    <Chip size="sm" className={toneOf(d.status)}>
                      {d.status}
                      {d.status === 'ready' && d.chunks ? ` · ${d.chunks} chunks` : ''}
                    </Chip>
                    {d.status === 'failed' && d.error && <div className="bdk-error">{d.error}</div>}
                  </td>
                  <td className="nyk-ui-num" data-label="Size">
                    {formatBytes(d.size)}
                  </td>
                  <td data-label="Uploaded">
                    {when(d.createdAt)}
                    {d.uploadedBy && <div className="nyk-ui-hint">{d.uploadedBy}</div>}
                  </td>
                  <td className="nyk-row-actions">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === d.id}
                      onClick={() =>
                        void act(d.id, async () => {
                          const { blob, fileName } = await api.download(d);
                          saveBlob(blob, fileName);
                        })
                      }
                    >
                      Download
                    </Button>
                    {canWrite && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy === d.id || indexing(d)}
                          onClick={() =>
                            void act(d.id, async () => {
                              const next = await api.reindex(d.id);
                              docs.setData((prev) => (prev ?? []).map((r) => (r.id === d.id ? next : r)));
                            })
                          }
                        >
                          Re-index
                        </Button>
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          disabled={busy === d.id}
                          message={`Delete ${d.title || d.fileName}? It is no longer found by search_docs.`}
                          onConfirm={() =>
                            void act(d.id, async () => {
                              await api.remove(d.id);
                              docs.setData((prev) => (prev ?? []).filter((r) => r.id !== d.id));
                            })
                          }
                        >
                          Delete
                        </ConfirmButton>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
    </div>
  );
}
